import { InView } from 'react-intersection-observer'
import React, { memo, MouseEvent, useCallback, useEffect, useRef } from 'react'
import {
  INCREASE_LIMIT_OFFSET,
  useItemsRenderLimit,
} from '$comp/_app/components/leftPanel/components/chat-list/react-hooks'
import { ForwardMenuItem } from '$comp/modals/forward-menu/components/forward-menu-item'
import { RoomListItem } from '$logic/room-list'
import { ForwardMenuItemsWrapper } from './components/forward-menu-item-wrapper/forward-menu-item-wrapper'

interface ListProps {
  items: RoomListItem[]
  selectionMap: Record<string, boolean>
  onSelectedChange: (selected: boolean, chatId: string) => void
}

export const SearchListRenderer = memo(function SearchListRendererComponent({
  items,
  selectionMap,
  onSelectedChange,
}: ListProps) {
  const selectOnTapRef = useRef<boolean>(false)
  const selectionMapRef = useRef<Record<string, boolean>>({})

  useEffect(() => {
    selectionMapRef.current = selectionMap
  }, [selectionMap])

  const onClick = useCallback(
    (chatId: string) => {
      const selected = !!selectionMapRef.current[chatId]

      if (selected) onSelectedChange(false, chatId)
      else onSelectedChange(true, chatId)
    },
    [onSelectedChange],
  )

  const onContextMenu = useCallback(
    (chatId: string) => (e?: MouseEvent) => {
      e?.preventDefault()
      if (!selectOnTapRef.current) onClick(chatId)
    },
    [onSelectedChange, onClick],
  )

  return (
    <ForwardMenuItemsWrapper>
      {items?.map(chatItem => (
        <ForwardMenuItem
          roomId={chatItem.chatId}
          key={chatItem.chatId}
          selected={!!selectionMap[chatItem.chatId]}
          onClick={onClick}
          onContextMenu={onContextMenu}
        />
      ))}
    </ForwardMenuItemsWrapper>
  )
})

export const ListRenderer = memo(function ListRendererComponent({
  items,
  selectionMap,
  onSelectedChange,
}: ListProps) {
  const selectOnTapRef = useRef<boolean>(false)
  const selectionMapRef = useRef<Record<string, boolean>>({})

  useEffect(() => {
    selectionMapRef.current = selectionMap
  }, [selectionMap])

  const onClick = useCallback(
    (chatId: string) => {
      const selected = !!selectionMapRef.current[chatId]

      if (selected) onSelectedChange(false, chatId)
      else onSelectedChange(true, chatId)
    },
    [onSelectedChange],
  )

  const onContextMenu = useCallback(
    (chatId: string) => (e?: MouseEvent) => {
      e?.preventDefault()
      if (!selectOnTapRef.current) onClick(chatId)
    },
    [onSelectedChange, onClick],
  )

  const { itemsRenderLimit, increaseItemsRenderLimit, increaseNeeded } =
    useItemsRenderLimit({ totalCount: items?.length ?? 0 })

  return (
    <ForwardMenuItemsWrapper>
      {items?.slice(0, itemsRenderLimit).map((chatItem, index) => (
        <>
          <ForwardMenuItem
            roomId={chatItem.chatId}
            key={chatItem.chatId}
            selected={selectionMap[chatItem.chatId]}
            onClick={onClick}
            onContextMenu={onContextMenu}
          />

          {increaseNeeded &&
            index === itemsRenderLimit - INCREASE_LIMIT_OFFSET - 1 && (
              <InView
                as="div"
                onChange={inView => {
                  if (inView) increaseItemsRenderLimit()
                }}
              />
            )}
        </>
      ))}
    </ForwardMenuItemsWrapper>
  )
})
